/**
 * FMD Generic Hero — Gate form + Flickity carousel + GreenLeaze price hydration
 * Custom element: <fmd-generic-hero>
 */
class FmdGenericHero extends HTMLElement {
  connectedCallback() {
    this.flickity = null;
    this.priceTimeout = null;
    this._flickityPollInterval = null;
    this.boundHydrate = this._hydrateGreenleazePrices.bind(this);
    this._boundEscHandler = (e) => {
      if (e.key === 'Escape') this._closeHelp();
    };

    this._fmdMonthlyAmount = 0;

    this._gate = this.querySelector('#fmd-gate');
    this._carWrapper = this.querySelector('#car-wrapper');
    this._companyInput = this.querySelector('#fmd-company');
    this._amountInput = this.querySelector('#fmd-input');
    this._emailInput = this.querySelector('#fmd-email');
    this._submitBtn = this.querySelector('#fmd-go');
    this._error = this.querySelector('#fmd-error');
    this._helpLink = this.querySelector('#fmd-help-link');
    this._helpPanel = this.querySelector('#fmd-help-panel');
    this._helpOverlay = this.querySelector('#fmd-help-overlay');
    this._helpClose = this.querySelector('#fmd-help-close');
    this._modifyBtn = this.querySelector('#fmd-modify');
    this._recapCompany = this.querySelector('#fmd-recap-company');
    this._recapVal = this.querySelector('#fmd-recap-val');
    this._recapMonth = this.querySelector('#fmd-recap-month');

    var stored = this._getStoredData();
    if (stored && this._companyInput && this._amountInput) {
      var amount = parseFloat(stored.amount);
      if (amount && amount >= 100 && amount <= 3500) {
        this._fmdMonthlyAmount = amount / 12;
        this._companyInput.value = stored.name || '';
        this._amountInput.value = amount;
        if (this._emailInput) this._emailInput.value = stored.email || '';
        this._populateRecap(stored.name || 'Mon entreprise', amount);
        this._revealCarousel();
      }
    }

    if (this._submitBtn) this._submitBtn.addEventListener('click', () => this._handleSubmit().catch(() => {}));
    if (this._modifyBtn) this._modifyBtn.addEventListener('click', () => this._resetGate());
    if (this._helpLink)
      this._helpLink.addEventListener('click', (e) => {
        e.preventDefault();
        this._openHelp();
      });
    if (this._helpClose) this._helpClose.addEventListener('click', () => this._closeHelp());
    if (this._helpOverlay) this._helpOverlay.addEventListener('click', () => this._closeHelp());
    document.addEventListener('keydown', this._boundEscHandler);
    if (this._companyInput)
      this._companyInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') this._amountInput.focus();
      });
    if (this._amountInput)
      this._amountInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') this._emailInput ? this._emailInput.focus() : this._handleSubmit().catch(() => {});
      });
    if (this._emailInput)
      this._emailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') this._handleSubmit().catch(() => {});
      });

    this._initStickyCta();
    this._waitForFlickity().then(() => this._initCarousel());
    this._initPriceHydration();
  }

  disconnectedCallback() {
    if (this.flickity) {
      this.flickity.destroy();
      this.flickity = null;
    }
    if (this.priceTimeout) {
      clearTimeout(this.priceTimeout);
    }
    if (this._flickityPollInterval) {
      clearInterval(this._flickityPollInterval);
    }
    document.removeEventListener('keydown', this._boundEscHandler);
    window.removeEventListener('greenleazePriceActualize', this.boundHydrate);
  }

  _getStoredData() {
    try {
      var raw = localStorage.getItem('fmd-data');
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed.timestamp && parsed.expiresIn) {
        if (Date.now() - parsed.timestamp > parsed.expiresIn) {
          localStorage.removeItem('fmd-data');
          localStorage.removeItem('fmd-generic-flow');
          return null;
        }
        return parsed.data;
      }
      return parsed.data || parsed;
    } catch (e) {
      return null;
    }
  }

  async _handleSubmit() {
    var company = this._companyInput.value.trim();
    var amount = parseFloat(this._amountInput.value);
    var email = this._emailInput ? this._emailInput.value.trim() : '';

    if (!company || !amount || amount < 100 || amount > 3500) {
      this._error.style.display = 'block';
      return;
    }
    this._error.style.display = 'none';

    // Tracking dataLayer (generate_fmd_form) — avant fetch n8n
    try {
      window.dataLayer = window.dataLayer || [];
      /** @type {Record<string, any>} */
      const payload = { event: 'generate_fmd_form' };
      const sha256Fn = /** @type {any} */ (window).sha256;
      if (email && typeof sha256Fn === 'function') {
        const emailHashed = await sha256Fn(email.toLowerCase().trim());
        payload.user_data = { email_hashed: emailHashed };
      }
      window.dataLayer.push(payload);
    } catch (e) {
      /* silent */
    }

    this._fmdMonthlyAmount = amount / 12;

    var fmdData = {
      data: { name: company, amount: String(amount), email: email },
      timestamp: Date.now(),
      expiresIn: 7 * 24 * 60 * 60 * 1000,
    };
    localStorage.setItem('fmd-data', JSON.stringify(fmdData));
    localStorage.setItem('fmd-generic-flow', 'true');

    // fire-and-forget — webhook n8n
    fetch('https://mintbikes.app.n8n.cloud/webhook/simulation_fmd', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ company: company, coverage: String(amount), email: email }),
    }).catch(function () {});

    this._populateRecap(company, amount);
    this._revealCarousel();
    this._hydrateGreenleazePrices();
  }

  _revealCarousel() {
    this._gate.style.display = 'none';
    this._carWrapper.style.display = 'block';
    if (this.flickity) this.flickity.resize();
  }

  _resetGate() {
    this._carWrapper.style.display = 'none';
    this._gate.style.display = 'block';
    if (this._amountInput) this._amountInput.focus();
  }

  _initStickyCta() {
    var stickyCtaLink = document.querySelector('.fmd-sticky-bar__cta');
    if (!stickyCtaLink || !this._gate) return;
    stickyCtaLink.addEventListener('click', (e) => {
      if (this._gate.style.display !== 'none') {
        e.preventDefault();
        this._gate.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (this._companyInput) this._companyInput.focus({ preventScroll: true });
      }
    });
  }

  _populateRecap(company, amount) {
    if (this._recapCompany) this._recapCompany.textContent = company;
    if (this._recapVal) this._recapVal.textContent = Math.round(amount) + ' €/an';
    if (this._recapMonth) this._recapMonth.textContent = Math.round(amount / 12) + ' €/mois';
  }

  _openHelp() {
    if (this._helpPanel) this._helpPanel.classList.add('open');
    if (this._helpOverlay) this._helpOverlay.classList.add('open');
  }

  _closeHelp() {
    if (this._helpPanel) this._helpPanel.classList.remove('open');
    if (this._helpOverlay) this._helpOverlay.classList.remove('open');
  }

  _waitForFlickity() {
    var self = this;
    return new Promise(function (resolve) {
      if (window.Flickity) {
        resolve();
        return;
      }
      var attempts = 0;
      self._flickityPollInterval = setInterval(function () {
        if (window.Flickity) {
          clearInterval(self._flickityPollInterval);
          self._flickityPollInterval = null;
          resolve();
        } else if (++attempts > 100) {
          clearInterval(self._flickityPollInterval);
          self._flickityPollInterval = null;
          resolve();
        }
      }, 100);
    });
  }

  _initCarousel() {
    var track = this.querySelector('.fmd-hero__carousel');
    if (!track || !window.Flickity) return;

    this.flickity = new window.Flickity(track, {
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      wrapAround: false,
      draggable: true,
      accessibility: true,
      resize: true,
      percentPosition: false,
    });

    var prevBtn = this.querySelector('.fmd-hero__carousel-btn--prev');
    var nextBtn = this.querySelector('.fmd-hero__carousel-btn--next');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.flickity.previous());
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.flickity.next());
    }

    this.flickity.on('change', () => {
      if (prevBtn) prevBtn.disabled = this.flickity.selectedIndex === 0;
      if (nextBtn) nextBtn.disabled = this.flickity.selectedIndex >= this.flickity.slides.length - 1;
    });

    if (prevBtn) prevBtn.disabled = true;
  }

  _initPriceHydration() {
    window.addEventListener('greenleazePriceActualize', this.boundHydrate);

    if (typeof window.getAllPricesForProduct === 'function') {
      this._hydrateGreenleazePrices();
    } else {
      this.priceTimeout = setTimeout(() => {
        this._hidePriceLines();
      }, 3000);
    }
  }

  _hydrateGreenleazePrices() {
    if (this.priceTimeout) {
      clearTimeout(this.priceTimeout);
      this.priceTimeout = null;
    }

    if (typeof window.getAllPricesForProduct !== 'function') return;

    var cards = this.querySelectorAll('[data-greenleaze-monthly]');
    cards.forEach((card) => this._hydrateCard(card));

    if (this.flickity) this.flickity.resize();
  }

  _hydrateCard(card) {
    var priceTTC = parseInt(card.dataset.productPrice, 10);
    var taxable = card.dataset.productTaxable === 'true';
    var taxRate = taxable ? 1.2 : 1;
    var priceHT = priceTTC / taxRate / 100;

    var prices = window.getAllPricesForProduct(priceHT);
    if (!prices || !prices[36] || !prices[36].monthly || prices[36].monthly <= 0) {
      card.style.display = 'none';
      return;
    }

    card.style.display = '';

    var monthly = prices[36].monthly;
    var monthlySpan = card.querySelector('[data-greenleaze-monthly-value]');
    if (monthlySpan) {
      monthlySpan.textContent = Math.round(monthly) + ' € / mois';
    }

    var cardEl = card.closest('.fmd-hero__card');
    if (!cardEl) return;
    var fmdMonthly = Math.min(this._fmdMonthlyAmount, monthly);

    var employerEl = cardEl.querySelector('[data-fmd-employer]');
    if (employerEl) {
      var greenSpan = employerEl.querySelector('.fmd-hero__card-line-green');
      if (greenSpan) {
        greenSpan.textContent = '-' + Math.round(fmdMonthly) + ' € / mois';
      }
    }

    var resteEl = cardEl.querySelector('[data-fmd-reste-a-charge]');
    if (resteEl) {
      var reste = Math.max(0, Math.round(monthly - fmdMonthly));
      var priceEl = resteEl.querySelector('.fmd-hero__card-reste-price');
      // safe: reste is always Math.round() integer
      if (priceEl) priceEl.innerHTML = reste + ' €<small> / mois</small>';
    }

    var economyEl = cardEl.querySelector('[data-fmd-economy]');
    if (economyEl) {
      var bikePrice = Math.round(priceTTC / 100);
      var economyTotal = Math.min(Math.round(fmdMonthly * 36), bikePrice);
      var valueEl = economyEl.querySelector('.fmd-hero__card-fmd-value');
      if (valueEl && economyTotal > 0) {
        valueEl.textContent = economyTotal + ' €';
      }
    }
  }

  _hidePriceLines() {
    var cards = this.querySelectorAll('[data-greenleaze-monthly]');
    cards.forEach((card) => {
      card.style.display = 'none';
    });
    var restes = this.querySelectorAll('[data-fmd-reste-a-charge]');
    restes.forEach((el) => {
      var priceEl = el.querySelector('.fmd-hero__card-reste-price');
      // safe: static string, no user input
      if (priceEl) priceEl.innerHTML = '- €<small> / mois</small>';
    });
  }
}

if (!customElements.get('fmd-generic-hero')) {
  customElements.define('fmd-generic-hero', FmdGenericHero);
}
